import React, { useEffect, useMemo, useState } from 'react';
import { toast as message } from 'sonner';
import { supabaseClient } from '../../utils/supabase';
import useAuth from '../../utils/useAuth';
import EnhancedDialog from '../EnhancedDialog';
import {
  OFFER_BLOCKED,
  cents,
  computeOffer,
  fetchOfferSender,
  fundingPackageHtml,
  money,
  properName,
  sendFundingPackage,
  unitFor,
  type OfferSender,
  type OfferTerms,
  type PrepayTier,
  type Structure,
} from './offerPackage';
import { RecipientList, RecipientTo, normalizeEmail } from './RecipientEditor';
import { useMerchantEmails } from './useMerchantEmails';
import './SendOfferDialog.css';

const STRUCTURES: Structure[] = ['Daily', 'Weekly', 'Bi-Weekly', 'Monthly'];
const ADMIN_CC = 'admin@skylinecaps.com';
const DEFAULT_OFFER_SUBJECT = 'Your Funding Package';

export interface SendOfferRow {
  id?: unknown;
  merchant?: unknown;
  email?: unknown;
  first_name?: unknown;
  merchant_label?: unknown;
  amount?: unknown;
  factor_rate?: unknown;
  term?: unknown;
  structure?: unknown;
}

interface TierDraft {
  factor: string;
  days: string;
}

function asNumberString(value: unknown): string {
  return typeof value === 'number' && Number.isFinite(value) ? String(value) : '';
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

export function SendOfferDialog({
  row,
  onClose,
}: {
  row: SendOfferRow | null;
  onClose: () => void;
}): React.ReactElement | null {
  const { appUser } = useAuth();
  const [step, setStep] = useState<'terms' | 'preview'>('terms');
  const [amount, setAmount] = useState('');
  const [factor, setFactor] = useState('');
  const [term, setTerm] = useState('');
  const [structure, setStructure] = useState<Structure>('Weekly');
  const [prepayMode, setPrepayMode] = useState<'tiers' | 'none'>('tiers');
  const [tiers, setTiers] = useState<TierDraft[]>([]);
  const [stips, setStips] = useState<string[]>([]);
  const [newStip, setNewStip] = useState('');
  const [repPhone, setRepPhone] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  // Editable envelope (REG-675): To is limited to the merchant's emails on
  // file, CC to internal addresses; admin@ is locked and the sender always
  // receives a copy — the edge function enforces the same rules.
  const [toEmail, setToEmail] = useState('');
  const [senderInCc, setSenderInCc] = useState(true);
  const [extraCc, setExtraCc] = useState<string[]>([]);
  const [subject, setSubject] = useState(DEFAULT_OFFER_SUBJECT);
  // REG-918: who the offer goes out as (Hilton reps send from their Hilton mailbox).
  const [sender, setSender] = useState<OfferSender | null>(null);
  const [senderError, setSenderError] = useState<string | null>(null);

  const isOpen = row != null;
  const rowEmail = asString(row?.email).trim().toLowerCase();
  const merchantId = typeof row?.merchant === 'number' ? row.merchant : null;
  const merchantEmails = useMerchantEmails(isOpen ? merchantId : null, rowEmail);
  const merchantEmail = toEmail || rowEmail;
  const businessName = asString(row?.merchant_label) || 'this merchant';
  const senderEmail = normalizeEmail(appUser?.email ?? '');
  const ccLocked = [ADMIN_CC, ...(senderInCc && senderEmail && senderEmail !== ADMIN_CC ? [] : [])];
  const ccEditable = [...(senderInCc && senderEmail && senderEmail !== ADMIN_CC ? [senderEmail] : []), ...extraCc];

  // Re-seed the form from the approval row each time the dialog opens.
  useEffect(() => {
    if (!row) return;
    setStep('terms');
    setToEmail(asString(row.email).trim().toLowerCase());
    setSenderInCc(true);
    setExtraCc([]);
    setSubject(DEFAULT_OFFER_SUBJECT);
    setAmount(asNumberString(row.amount));
    setFactor(asNumberString(row.factor_rate));
    setTerm(asNumberString(row.term));
    const rowStructure = asString(row.structure) as Structure;
    setStructure(STRUCTURES.includes(rowStructure) ? rowStructure : 'Weekly');
    setPrepayMode('tiers');
    setTiers([{ factor: '1.30', days: '30' }, { factor: '1.35', days: '60' }]);
    setStips(["Driver's License", 'Voided Check']);
    setNewStip('');
    setSending(false);
  }, [row]);

  // REG-918: ask the server who this offer goes out as, for the address picked.
  // The send-rep-email function asks the same question before it queues.
  useEffect(() => {
    if (!isOpen || merchantId == null) return;
    let cancelled = false;
    setSenderError(null);
    fetchOfferSender(merchantId, merchantEmail || null)
      .then((answer) => {
        if (!cancelled) setSender(answer);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setSender(null);
        setSenderError(err instanceof Error ? err.message : 'Could not check who this offer goes out as');
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, merchantId, merchantEmail]);
  const hilton = sender?.brand === 'HILTON';
  const blockedReason = hilton && sender?.blocked ? OFFER_BLOCKED[sender.blocked] ?? 'This recipient is not allowed.' : null;

  // The signature phone lives on the rep record; fetch lazily so the grid load stays untouched.
  useEffect(() => {
    if (!isOpen || !appUser?.id) return;
    let cancelled = false;
    supabaseClient
      .from('rep')
      .select('phone')
      .eq('app_user', appUser.id)
      .limit(1)
      .then(({ data }) => {
        if (!cancelled) setRepPhone(data?.[0]?.phone ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, appUser?.id]);

  const terms: OfferTerms | null = useMemo(() => {
    const amountN = parseFloat(amount);
    const factorN = parseFloat(factor);
    const termN = parseInt(term, 10);
    if (!(amountN > 0) || !(factorN > 0) || !(termN > 0)) return null;
    const prepays: PrepayTier[] = prepayMode === 'tiers'
      ? tiers
        .map((t) => ({ factor: parseFloat(t.factor), days: parseInt(t.days, 10) }))
        .filter((t) => t.factor > 0 && t.days > 0)
      : [];
    return { amount: amountN, factor: factorN, term: termN, structure, prepayMode, prepays, stips };
  }, [amount, factor, term, structure, prepayMode, tiers, stips]);

  const previewHtml = useMemo(() => {
    if (!terms) return '';
    if (hilton && sender?.from_email) {
      return fundingPackageHtml(terms, asString(row?.first_name),
        { name: sender.from_name ?? '', email: sender.from_email, phone: sender.phone }, 'HILTON');
    }
    const rep = appUser?.email
      ? { name: appUser.name ?? '', email: appUser.email, phone: repPhone ?? undefined }
      : null;
    return fundingPackageHtml(terms, asString(row?.first_name), rep);
  }, [terms, appUser?.email, appUser?.name, repPhone, row?.first_name, hilton, sender]);

  if (!isOpen) return null;

  const computed = terms ? computeOffer(terms) : null;

  const handleSend = async () => {
    if (!terms || !merchantEmail || !appUser?.email || merchantId == null || sending || !sender || blockedReason) return;
    setSending(true);
    try {
      // The edge function authenticates the rep's own session and derives the
      // From address server-side — the rep can only send as themselves.
      const result = await sendFundingPackage({
        merchantId,
        merchantEmail,
        html: previewHtml,
        subject: subject.trim() || DEFAULT_OFFER_SUBJECT,
        cc: extraCc,
        senderInCc,
        kind: 'offer',
      });
      if (!result.ok) throw new Error(result.error || 'The email service rejected the send');
      message.success(result.brand === 'HILTON'
        ? `Funding package sent to ${result.to ?? merchantEmail} from ${result.from}`
        : `Funding package sent to ${merchantEmail} (from ${result.from ?? appUser.email}, CC ${(result.cc ?? [ADMIN_CC]).join(', ')})`);
      onClose();
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : 'Could not send the funding package');
    } finally {
      setSending(false);
    }
  };

  const addStip = () => {
    const name = newStip.trim();
    if (!name) return;
    if (!stips.some((s) => s.toLowerCase() === name.toLowerCase())) {
      setStips([...stips, name]);
    }
    setNewStip('');
  };

  return (
    <EnhancedDialog
      isOpen={isOpen}
      onClose={sending ? () => {} : onClose}
      title={step === 'terms' ? `Send offer — ${businessName}` : `Preview — ${businessName}`}
      className="send-offer-dialog"
      // Reps lose in-progress terms/prepays edits on accidental dismissal:
      // these email dialogs only close through Cancel / the X (REG: Zack 8/28).
      canOutsideClickClose={false}
      canEscapeKeyClose={false}
    >
      {step === 'terms' ? (
        <>
          <div className="sky-dialog-body">
            {!merchantEmail && (
              <div className="sky-callout sky-callout-warning" style={{ marginBottom: 12 }}>
                This merchant has no email address on file — add one to the merchant record before sending.
              </div>
            )}

            <div className="send-offer-section-label">Terms</div>
            <div className="send-offer-grid2">
              <div className="sky-form-group">
                <label className="sky-form-label">Dollar amount</label>
                <input className="sky-field-input" type="number" min="1" value={amount}
                  onChange={(e) => setAmount(e.target.value)} placeholder="15000" />
              </div>
              <div className="sky-form-group">
                <label className="sky-form-label">Factor</label>
                <input className="sky-field-input" type="number" step="0.001" min="1" value={factor}
                  onChange={(e) => setFactor(e.target.value)} placeholder="1.499" />
              </div>
              <div className="sky-form-group">
                <label className="sky-form-label">Term <span className="send-offer-hint">· number of payments</span></label>
                <input className="sky-field-input" type="number" min="1" value={term}
                  onChange={(e) => setTerm(e.target.value)} placeholder="90" />
              </div>
              <div className="sky-form-group">
                <label className="sky-form-label">Structure</label>
                <select className="sky-field-input" value={structure}
                  onChange={(e) => setStructure(e.target.value as Structure)}>
                  {STRUCTURES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            {terms && computed && (
              <div className="send-offer-summary">
                {money(terms.amount)} × {terms.factor} ={' '}
                <b>{money(computed.payback)}</b> payback · <b>{cents(computed.payment)}</b> /{' '}
                {structure.toLowerCase()} × {terms.term} {unitFor(structure)}
              </div>
            )}

            <div className="send-offer-section-label">Prepayment</div>
            <div className="send-offer-toggle">
              <button type="button"
                className={prepayMode === 'tiers' ? 'send-offer-toggle-btn active' : 'send-offer-toggle-btn'}
                onClick={() => setPrepayMode('tiers')}>Prepay tiers</button>
              <button type="button"
                className={prepayMode === 'none' ? 'send-offer-toggle-btn active' : 'send-offer-toggle-btn'}
                onClick={() => setPrepayMode('none')}>No prepays</button>
            </div>
            {prepayMode === 'tiers' && (
              <div className="send-offer-tiers">
                {tiers.map((tier, i) => (
                  <div className="send-offer-tier-row" key={i}>
                    <input className="sky-field-input" type="number" step="0.01" min="1" value={tier.factor}
                      placeholder="Factor (e.g. 1.30)"
                      onChange={(e) => setTiers(tiers.map((t, j) => (j === i ? { ...t, factor: e.target.value } : t)))} />
                    <input className="sky-field-input" type="number" min="1" value={tier.days}
                      placeholder="Days (e.g. 30)"
                      onChange={(e) => setTiers(tiers.map((t, j) => (j === i ? { ...t, days: e.target.value } : t)))} />
                    <button type="button" className="send-offer-remove" aria-label="Remove prepay tier"
                      onClick={() => setTiers(tiers.filter((_, j) => j !== i))}>×</button>
                  </div>
                ))}
                <button type="button" className="sky-btn sky-btn-ghost send-offer-add-tier"
                  onClick={() => setTiers([...tiers, { factor: '', days: '' }])}>+ Add prepay tier</button>
              </div>
            )}

            <div className="send-offer-section-label">Required stipulations</div>
            <div className="send-offer-stips">
              {stips.map((stip) => (
                <span className="send-offer-stip" key={stip}>
                  {stip}
                  <button type="button" aria-label={`Remove ${stip}`}
                    onClick={() => setStips(stips.filter((s) => s !== stip))}>×</button>
                </span>
              ))}
            </div>
            <div className="send-offer-stip-add">
              <input className="sky-field-input" value={newStip} placeholder="Add another document (e.g. Bank statements)"
                onChange={(e) => setNewStip(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addStip(); } }} />
              <button type="button" className="sky-btn sky-btn-ghost" onClick={addStip}>Add</button>
            </div>
          </div>
          <div className="sky-dialog-footer">
            <button className="sky-btn sky-btn-ghost" onClick={onClose}>Cancel</button>
            <button className="sky-btn sky-btn-primary" disabled={!terms || !merchantEmail}
              onClick={() => setStep('preview')}>Preview email</button>
          </div>
        </>
      ) : (
        <>
          <div className="sky-dialog-body">
            <div className="send-offer-envelope">
              <RecipientTo options={merchantEmails} value={merchantEmail} onChange={setToEmail} disabled={sending} />
              {hilton ? (
                <>
                  <div data-testid="send-offer-from"><span className="send-offer-envelope-label">From</span> {sender?.from_name} &lt;{sender?.from_email}&gt;</div>
                  <div data-testid="send-offer-hilton-cc"><span className="send-offer-envelope-label">CC</span> None. Hilton offers go to the merchant only; admin@ gets a hidden copy and yours is in your Hilton Sent folder.</div>
                </>
              ) : (
                <>
                  <div data-testid="send-offer-from"><span className="send-offer-envelope-label">From</span> {properName(appUser?.name ?? '')} &lt;{appUser?.email}&gt;</div>
                  <RecipientList
                    label="CC"
                    locked={ccLocked}
                    editable={ccEditable}
                    disabled={sending}
                    onChange={(next) => {
                      // Removing yourself from CC only hides you: the server still
                      // Bcc's the sender a copy.
                      setSenderInCc(Boolean(senderEmail) && next.includes(senderEmail));
                      setExtraCc(next.filter((email) => email !== senderEmail));
                    }}
                  />
                </>
              )}
              <div className="send-offer-recipients">
                <span className="send-offer-envelope-label">Subject</span>
                <input
                  className="send-offer-subject-input"
                  aria-label="Email subject"
                  value={subject}
                  maxLength={200}
                  disabled={sending}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>
            </div>
            {hilton && sender?.sandbox && !blockedReason && (
              <div className="sky-callout sky-callout-warning" style={{ margin: '8px 0' }} data-testid="send-offer-sandbox">
                Sandbox: this offer is delivered only to {sender.delivered_to}.
              </div>
            )}
            {(blockedReason || senderError) && (
              <div className="sky-callout sky-callout-danger" style={{ margin: '8px 0' }} data-testid="send-offer-blocked">
                {blockedReason ? `Offer can't be sent: ${blockedReason}` : senderError}
              </div>
            )}
            <iframe className="send-offer-preview" title="Funding package email preview"
              sandbox="" srcDoc={previewHtml} />
          </div>
          <div className="sky-dialog-footer">
            <button className="sky-btn sky-btn-ghost" onClick={() => setStep('terms')} disabled={sending}>Back</button>
            <button className="sky-btn sky-btn-primary" onClick={handleSend}
              disabled={sending || !merchantEmail || !subject.trim() || !sender || Boolean(blockedReason)} aria-busy={sending}>
              {sending ? <span className="sky-spinner" /> : null}
              {sending ? 'Sending…' : 'Send email'}
            </button>
          </div>
        </>
      )}
    </EnhancedDialog>
  );
}

export default SendOfferDialog;
